import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { wordpressAPI } from '../services/wordpressApi';
import { WPPost } from '../types/wordpress';
import { ClinicalArea, Topic } from '../types/metadata';
import {
  ORBCON_REPORT_CATEGORY_ID,
  ORBCON_REPORT_TITLE,
  groupReportIssues,
} from '../content/orbconReport';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import NewsletterSignup from '../components/home/NewsletterSignup';
import ReportFilters from '../components/report/ReportFilters';
import { getPostMetadata } from '../services/mockMetadata';
import { buildResourcePath } from '../utils/resourceLinks';
import { stripHtml } from '../utils/htmlEntities';
import { format } from 'date-fns';
import { usePageTitle } from '../hooks/usePageTitle';

const PAST_ISSUES_PAGE = 6;

async function loadReportPosts(): Promise<WPPost[]> {
  const posts: WPPost[] = [];
  for (let page = 1; page <= 10; page += 1) {
    try {
      const batch = await wordpressAPI.fetchPosts({
        categories: [ORBCON_REPORT_CATEGORY_ID],
        per_page: 100,
        page,
        orderby: 'date',
        order: 'desc',
      });
      posts.push(...batch);
      if (batch.length < 100) break;
    } catch (err) {
      if (posts.length > 0) break;
      throw err;
    }
  }
  return posts;
}

function IssueArticles({ posts }: { posts: WPPost[] }) {
  return (
    <ul className="space-y-3">
      {posts.map((post) => (
        <li key={post.id}>
          <Link
            to={buildResourcePath(post.slug)}
            className="block rounded-md focus-ring"
          >
            <span className="text-base font-medium text-gray-900 hover:text-primary">
              {stripHtml(post.title.rendered)}
            </span>
            <span className="block text-sm text-gray-500 mt-1">
              {format(new Date(post.date), 'MMMM d, yyyy')}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function postYear(post: WPPost): string | null {
  const year = new Date(post.date).getFullYear();
  return Number.isFinite(year) ? String(year) : null;
}

function selectedValues<T extends string>(param: string | null, allowed: readonly T[]): T[] {
  if (!param) return [];
  const known = new Set(allowed);
  return param.split(',').filter((value): value is T => known.has(value as T));
}

function toggleValue(params: URLSearchParams, key: string, value: string): URLSearchParams {
  const next = new URLSearchParams(params);
  const current = (next.get(key) || '').split(',').filter(Boolean);
  const updated = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
  if (updated.length === 0) next.delete(key);
  else next.set(key, updated.join(','));
  return next;
}

export default function OrbconReport() {
  const [posts, setPosts] = useState<WPPost[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [visiblePast, setVisiblePast] = useState(PAST_ISSUES_PAGE);
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle(ORBCON_REPORT_TITLE);

  const searchQuery = searchParams.get('search') || '';
  const selectedTopics = useMemo(
    () => selectedValues(searchParams.get('topics'), Object.values(Topic)),
    [searchParams]
  );
  const selectedClinicalAreas = useMemo(
    () => selectedValues(searchParams.get('clinicalAreas'), Object.values(ClinicalArea)),
    [searchParams]
  );
  const selectedYears = useMemo(() => {
    if (!posts) return [];
    const known = posts.map(postYear).filter((year): year is string => year !== null);
    return selectedValues(searchParams.get('years'), known);
  }, [posts, searchParams]);
  const filtering =
    searchQuery.trim().length > 0 ||
    selectedYears.length > 0 ||
    selectedTopics.length > 0 ||
    selectedClinicalAreas.length > 0;

  useEffect(() => {
    let cancelled = false;
    loadReportPosts()
      .then((loaded) => {
        if (!cancelled) setPosts(loaded);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load the ORBCoN Report'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setVisiblePast(PAST_ISSUES_PAGE);
  }, [searchParams]);

  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    const query = searchQuery.trim().toLowerCase();
    return posts.filter((post) => {
      if (query) {
        const title = stripHtml(post.title.rendered).toLowerCase();
        const excerpt = stripHtml(post.excerpt?.rendered).toLowerCase();
        if (!title.includes(query) && !excerpt.includes(query)) return false;
      }
      if (selectedYears.length > 0 && !selectedYears.includes(postYear(post) ?? '')) return false;
      const metadata = getPostMetadata(post);
      if (selectedTopics.length > 0 && !metadata.topics?.some((topic) => selectedTopics.includes(topic))) {
        return false;
      }
      if (
        selectedClinicalAreas.length > 0 &&
        !metadata.clinicalAreas?.some((area) => selectedClinicalAreas.includes(area))
      ) {
        return false;
      }
      return true;
    });
  }, [posts, searchQuery, selectedYears, selectedTopics, selectedClinicalAreas]);

  const issues = useMemo(() => groupReportIssues(filteredPosts), [filteredPosts]);
  const current = filtering ? undefined : issues[0];
  const past = filtering ? issues : issues.slice(1);
  const shownPast = past.slice(0, visiblePast);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: ORBCON_REPORT_TITLE }]} />
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{ORBCON_REPORT_TITLE}</h1>
        <p className="text-gray-600 mt-2">
          Articles from the monthly ORBCoN Report.
        </p>
      </header>

      {error && (
        <ErrorState
          error={error}
          title="Could not load the ORBCoN Report"
          onRetry={() => window.location.reload()}
        />
      )}

      {!error && posts === null && (
        <div className="space-y-4">
          <LoadingSkeleton lines={4} />
          <LoadingSkeleton lines={4} />
        </div>
      )}

      {posts && (
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:sticky lg:top-32 lg:h-fit">
            <ReportFilters
              posts={posts}
              searchQuery={searchQuery}
              selectedYears={selectedYears}
              selectedTopics={selectedTopics}
              selectedClinicalAreas={selectedClinicalAreas}
              onSearchChange={(query) => {
                const next = new URLSearchParams(searchParams);
                if (query.trim()) next.set('search', query);
                else next.delete('search');
                setSearchParams(next, { replace: true });
              }}
              onYearToggle={(year) => setSearchParams(toggleValue(searchParams, 'years', year), { replace: true })}
              onTopicToggle={(topic) => setSearchParams(toggleValue(searchParams, 'topics', topic), { replace: true })}
              onClinicalAreaToggle={(area) =>
                setSearchParams(toggleValue(searchParams, 'clinicalAreas', area), { replace: true })
              }
              onClear={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('search');
                next.delete('years');
                next.delete('topics');
                next.delete('clinicalAreas');
                setSearchParams(next, { replace: true });
              }}
            />
          </div>
          <div className="flex-1 min-w-0">
            {filtering && issues.length === 0 && (
              <p className="text-gray-600 mb-10">No articles match these filters.</p>
            )}

            {current && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4 mb-4">
                  Current issue
                </h2>
                <Card>
                  <p className="text-sm font-semibold text-gray-500 mb-3">{current.label}</p>
                  <IssueArticles posts={current.posts} />
                </Card>
              </section>
            )}

            {!filtering && issues.length === 0 && (
              <p className="text-gray-600 mb-10">No issues published yet.</p>
            )}

            {past.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#00467f] pl-4 mb-4">
                  {filtering ? 'Matching articles' : 'Past issues'}
                </h2>
                <div className="space-y-6">
                  {shownPast.map((issue) => (
                    <div key={issue.label}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{issue.label}</h3>
                      <IssueArticles posts={issue.posts} />
                    </div>
                  ))}
                </div>
                {shownPast.length < past.length && (
                  <button
                    type="button"
                    onClick={() => setVisiblePast((count) => count + PAST_ISSUES_PAGE)}
                    className="mt-6 min-h-[44px] px-4 text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
                  >
                    Show more ({past.length - shownPast.length} remaining)
                  </button>
                )}
              </section>
            )}

            <section className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscribe</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sign up for the monthly newsletter, the ORBCoN Report.
              </p>
              <NewsletterSignup tone="default" />
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
